// Saved places: map bookmarks (existing places or named custom spots) plus a
// "nearby activity" sub-tab — recent outings around the saved points. Owner-
// private like the ticklist: RLS returns only your own rows.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "wouter";
import { Check, Map as MapIcon, MapPin, Pencil, X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  DELETE_SAVED_PLACE,
  FEED,
  MY_SAVED_PLACES,
  UPDATE_SAVED_PLACE,
  type FeedResult,
  type MySavedPlacesResult,
  type SavedPlaceEntry,
} from "../lib/queries";
import { ACTIVITIES, ACTIVITY_ICONS, formatDate, PLACE_TYPE_ICONS, type Activity } from "../lib/format";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { savedEntryCoords } from "../components/SavedPlacesMenu";
import { LoginGate } from "../components/LoginGate";
import { OutingCard } from "../components/OutingCard";
import { Chip, EmptyState, ErrorBox, Spinner, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";

const ACTIVITY_PAGE = 30;

// Half-side of the box drawn around each saved point for the nearby-activity
// filter, in degrees (~11 km of latitude).
const NEAR_DEG = 0.1;

// Cap on how many saved points go into one "nearby" filter — each point adds
// two OR branches (own track + linked route's main track), and an unbounded
// list would balloon the query for heavy savers.
const NEAR_MAX_POINTS = 20;

export function SavedPage() {
  return (
    <LoginGate action="gateAction.keepSaved" next="/saved">
      <Saved />
    </LoginGate>
  );
}

/** An outing is "near" a point if its own track or a linked route's main track intersects the box around it. */
function nearFilter(coords: [number, number][]): Record<string, unknown> {
  const clauses = coords.slice(0, NEAR_MAX_POINTS).flatMap(([lng, lat]) => {
    const box = { minX: lng - NEAR_DEG, minY: lat - NEAR_DEG, maxX: lng + NEAR_DEG, maxY: lat + NEAR_DEG };
    return [
      { track: { geom: { bboxIntersects: box } } },
      {
        outingRoutesByOutingId: {
          some: {
            route: {
              routeTracksByRouteId: {
                some: { role: { equalTo: "MAIN" }, track: { geom: { bboxIntersects: box } } },
              },
            },
          },
        },
      },
    ];
  });
  return { or: clauses };
}

function Saved() {
  const { personId } = useAuth();
  const { t, labelizeT, entityName } = useT();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);

  const tab = params.get("tab") === "activity" ? "activity" : "places";
  const setTab = (next: "places" | "activity") =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "activity") p.set("tab", "activity");
        else p.delete("tab");
        return p;
      },
      { replace: true },
    );

  const { data, isLoading, error } = useQuery({
    queryKey: ["saved", personId],
    queryFn: () => gqlRequest<MySavedPlacesResult>(MY_SAVED_PLACES, { personId }),
  });
  const entries = data?.savedPlaces.nodes ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["saved", personId] });

  const rename = useMutation({
    mutationFn: (vars: { id: string; name: string }) => gqlRequest(UPDATE_SAVED_PLACE, vars),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => gqlRequest(DELETE_SAVED_PLACE, { id }),
    onSuccess: () => {
      invalidate();
      // The place-page button caches its row id under its own key.
      queryClient.invalidateQueries({ queryKey: ["saved-for-place"] });
    },
  });

  const tabClass = (active: boolean) =>
    `rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
      active ? "bg-card text-foreground shadow-card" : "text-muted-foreground hover:text-foreground"
    }`;

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  return (
    <div className="mx-auto max-w-2xl p-4">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("saved.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("saved.intro")}</p>
      </div>

      <div className="mb-4 inline-flex gap-1 rounded-xl bg-muted p-1" role="tablist" aria-label={t("saved.title")}>
        <button type="button" role="tab" aria-selected={tab === "places"} className={tabClass(tab === "places")} onClick={() => setTab("places")}>
          {t("saved.tabPlaces")}
        </button>
        <button type="button" role="tab" aria-selected={tab === "activity"} className={tabClass(tab === "activity")} onClick={() => setTab("activity")}>
          {t("saved.tabActivity")}
        </button>
      </div>

      {(rename.error ?? remove.error) != null && <ErrorBox error={rename.error ?? remove.error} />}

      {entries.length === 0 && (
        <EmptyState>
          {t("saved.empty")}{" "}
          <Link href="/" className="font-medium text-primary transition-colors hover:underline">
            {t("saved.browseMap")}
          </Link>
          .
        </EmptyState>
      )}

      {tab === "places" && entries.length > 0 && (
        <ul className="space-y-2">
          {entries.map((entry) => (
            <SavedRow
              key={entry.id}
              entry={entry}
              busy={rename.isPending || remove.isPending}
              onRename={(name) => rename.mutate({ id: entry.id, name })}
              onRemove={() => remove.mutate(entry.id)}
            />
          ))}
        </ul>
      )}

      {tab === "activity" && entries.length > 0 && (
        <>
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
            <Chip active={scopeId === null} onClick={() => setScopeId(null)}>
              {t("saved.allSaved")}
            </Chip>
            {entries.map((entry) => (
              <Chip
                key={entry.id}
                active={scopeId === entry.id}
                onClick={() => setScopeId((prev) => (prev === entry.id ? null : entry.id))}
              >
                {entry.place ? entityName(entry.place) : entry.name}
              </Chip>
            ))}
          </div>
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
            {ACTIVITIES.map((a) => {
              const Icon = ACTIVITY_ICONS[a];
              return (
                <Chip
                  key={a}
                  active={activities.includes(a)}
                  onClick={() => setActivities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]))}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(a)}
                  </span>
                </Chip>
              );
            })}
          </div>
          <NearbyActivity entries={entries} scopeId={scopeId} activities={activities} />
        </>
      )}
    </div>
  );
}

function SavedRow({
  entry,
  busy,
  onRename,
  onRemove,
}: {
  entry: SavedPlaceEntry;
  busy: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
}) {
  const { t, labelizeT, entityName } = useT();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(entry.name ?? "");
  const coords = savedEntryCoords(entry);
  const Icon = entry.place ? (PLACE_TYPE_ICONS[entry.place.type] ?? MapPin) : MapPin;

  return (
    <li className="rounded-xl border bg-card p-3 text-card-foreground shadow-card transition-all duration-150 hover:border-muted-foreground/30 hover:shadow-card-hover">
      <div className="flex items-center gap-3">
        <Icon aria-hidden className="size-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          {editing ? (
            <form
              className="flex items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                if (!name.trim()) return;
                onRename(name.trim());
                setEditing(false);
              }}
            >
              <TextInput autoFocus value={name} onChange={(e) => setName(e.target.value)} className="!py-1 !text-sm" />
              <Button
                type="submit"
                disabled={!name.trim() || busy}
                aria-label={t("common.save")}
                variant="outline"
                size="icon-sm"
              >
                <Check aria-hidden className="size-3.5" />
              </Button>
            </form>
          ) : entry.place ? (
            <Link
              href={`/place/${entry.place.id}`}
              className="block truncate font-medium text-foreground transition-colors hover:text-primary"
            >
              {entityName(entry.place)}
            </Link>
          ) : (
            <p className="truncate font-medium text-foreground">{entry.name}</p>
          )}
          <p className="mt-0.5 text-xs text-muted-foreground">
            {entry.place ? labelizeT(entry.place.type) : t("saved.customPin")}
            {entry.place?.elevation != null && ` · ${entry.place.elevation} m`}
            {!entry.place && coords && ` · ${coords[1].toFixed(4)}, ${coords[0].toFixed(4)}`}
            {` · ${t("saved.savedOn", { date: formatDate(entry.createdAt) })}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {coords && (
            <Button asChild variant="outline" size="sm" className="border-primary/20 bg-primary/5 text-primary hover:bg-primary/10">
              <Link
                href={`/?lng=${coords[0].toFixed(4)}&lat=${coords[1].toFixed(4)}&z=14`}
                title={t("saved.showOnMapTitle")}
              >
                <MapIcon aria-hidden className="size-3.5" />
                {t("saved.showOnMap")}
              </Link>
            </Button>
          )}
          {!entry.place && !editing && (
            <Button
              type="button"
              onClick={() => {
                setName(entry.name ?? "");
                setEditing(true);
              }}
              disabled={busy}
              title={t("saved.renameTitle")}
              aria-label={t("saved.renameTitle")}
              variant="outline"
              size="icon-sm"
            >
              <Pencil aria-hidden className="size-3.5" />
            </Button>
          )}
          <Button
            type="button"
            onClick={onRemove}
            disabled={busy}
            title={t("saved.removeTitle")}
            aria-label={t("saved.removeTitle")}
            variant="outline"
            size="icon-sm"
            className="hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
          >
            <X aria-hidden className="size-3.5" />
          </Button>
        </div>
      </div>
    </li>
  );
}

function NearbyActivity({
  entries,
  scopeId,
  activities,
}: {
  entries: SavedPlaceEntry[];
  scopeId: string | null;
  activities: Activity[];
}) {
  const { t } = useT();
  const scoped = scopeId ? entries.filter((e) => e.id === scopeId) : entries;
  const coords = scoped.map(savedEntryCoords).filter((c): c is [number, number] => c != null);

  const filter: Record<string, unknown> = nearFilter(coords);
  if (activities.length) filter.activity = { in: activities };

  const { data, isLoading, error } = useQuery({
    queryKey: ["saved-activity", scopeId, activities, coords],
    enabled: coords.length > 0,
    queryFn: () =>
      gqlRequest<FeedResult>(FEED, {
        first: ACTIVITY_PAGE,
        offset: 0,
        filter,
        orderBy: ["DATE_DESC"],
      }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const outings = data?.outings.nodes ?? [];
  const total = data?.outings.totalCount ?? 0;

  return (
    <>
      <p className="mb-3 text-xs text-muted-foreground">{t("saved.nearbyNote")}</p>
      {outings.length === 0 && <EmptyState>{t("saved.emptyActivity")}</EmptyState>}
      <div className="space-y-3">
        {outings.map((outing) => (
          <OutingCard key={outing.id} outing={outing} />
        ))}
      </div>
      {total > outings.length && (
        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t("saved.latest", { count: outings.length })}
        </p>
      )}
    </>
  );
}
